import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import moment from 'moment';
import { DatePicker, Input } from 'syngenta-digital-cropwise-react-ui-kit';
import { set } from 'lodash';
import { getCropzonesCropArea } from '../../helpers/propertyHelpers';

const { RangePicker } = DatePicker;

const DATE_FORMAT = 'MM/DD/YYYY';

export const errorId = htmlFor => `${htmlFor}-error`;

// The error sits absolutely so a validation message never reflows the form,
// which is how the grow-ui field this replaces behaved. `children` is a render
// prop so the control can wire itself to the message it owns.
const FormField = ({ label, htmlFor, error = '', children }) => (
  <div className="relative my-4 w-full">
    <label className="text-gray-600 font-medium" htmlFor={htmlFor}>
      {label}
    </label>
    {children({
      'aria-invalid': Boolean(error),
      'aria-describedby': error ? errorId(htmlFor) : undefined
    })}
    {error && (
      <div
        id={errorId(htmlFor)}
        role="alert"
        className="absolute my-1 text-xs truncate text-red-600"
      >
        {error}
      </div>
    )}
  </div>
);

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  htmlFor: PropTypes.string.isRequired,
  error: PropTypes.string,
  children: PropTypes.func.isRequired
};

const formSectionDefaultPropProperties = [];
const FormSection = ({
  properties = formSectionDefaultPropProperties,
  propertiesLoading = true,
  setFieldValue,
  values,
  setTotalArea,
  setGeoJSONCollectionFeatures,
  fieldsAndAreasGeoJSONCollection
}) => {
  const { errors, touched, setFieldTouched } = useFormikContext();

  const nameError = touched.name && errors.name ? errors.name : '';
  const dateError = touched.date && errors.date ? errors.date : '';

  const dateRange = values.date?.length
    ? [moment(values.date[0]), moment(values.date[1] ?? values.date[0])]
    : null;

  const handleDateChange = dates => {
    setFieldTouched('date', true);
    setFieldValue(
      'date',
      dates?.every(Boolean) ? dates.map(date => date.toDate()) : null
    );
  };

  let temp = 0;
  const getCropZonesData = cropZones => {
    getCropzonesCropArea(cropZones).then(results => {
      setTotalArea(
        results.reduce((acc, { reportedArea, boundaryArea }) => {
          temp = acc + (reportedArea || boundaryArea);
          return temp;
        }, 0)
      );
    });
  };

  const filterFeatureCollectionByCropZones = (fc, ids) => {
    return fc.features
      ? fc.features.map(feat => {
          set(
            feat,
            'properties.$layer',
            ids?.includes(feat.properties?.id) ||
              (ids?.includes(feat.properties?.id) &&
                !feat.properties?.$parentDuplicated)
              ? 'focused'
              : 'default'
          );
          return feat;
        })
      : [];
  };

  const updateZoom = featureLayer =>
    setGeoJSONCollectionFeatures(
      fieldsAndAreasGeoJSONCollection.features
        ? fieldsAndAreasGeoJSONCollection.features.map(feat => {
            set(feat, 'properties.$layer', featureLayer);
            return feat;
          })
        : []
    );

  const updateSelectedProperties = (cropZoneIds, farmIds) =>
    properties
      .filter(({ id }) => farmIds.includes(id))
      .map(property => {
        const propertyCropZones = property.fields.flatMap(
          ({ cropzones }) => cropzones
        );
        return {
          farmId: property.id,
          cropzoneIds: propertyCropZones
            .filter(({ id }) => cropZoneIds.includes(id))
            .map(({ id }) => id)
        };
      });

  const handlePropertySelect = (cropZoneIds, farmIds) => {
    setGeoJSONCollectionFeatures(
      filterFeatureCollectionByCropZones(
        fieldsAndAreasGeoJSONCollection,
        cropZoneIds
      )
    );
    const selectedProperties = updateSelectedProperties(cropZoneIds, farmIds);
    getCropZonesData(cropZoneIds);
    setFieldValue('properties', selectedProperties);

    // Zooming out map in case the properties checklist is completely unchecked.
    if (
      !propertiesLoading &&
      properties.length !== 0 &&
      selectedProperties.length === 0
    ) {
      updateZoom('focused'); // setting as 'focused' to zoom out first.
      updateZoom('default'); // then setting as 'default' to update CSS.
    }
  };

  useEffect(() => {
    if (values.cropZones) {
      handlePropertySelect(values.cropZones, values.farms);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertiesLoading]);

  return (
    <div
      data-testid="cropseason-sidebar"
      className="flex-col items-center justify-center flex-none p-6 bg-white"
      style={{ width: '500px' }}
    >
      <FormField label="Crop Season Name*" htmlFor="name" error={nameError}>
        {a11y => (
          <Input
            id="name"
            name="name"
            data-testid="cropSeasonName"
            value={values.name ?? ''}
            status={nameError ? 'error' : ''}
            onChange={event => setFieldValue('name', event.target.value)}
            onBlur={() => setFieldTouched('name', true)}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...a11y}
          />
        )}
      </FormField>
      <FormField label="Crop Season Dates*" htmlFor="date" error={dateError}>
        {a11y => (
          <RangePicker
            id="date"
            name="date"
            data-testid="cropSeasonDate"
            className="w-full"
            format={DATE_FORMAT}
            placeholder={['Start date', 'End date']}
            status={dateError ? 'error' : ''}
            value={dateRange}
            onChange={handleDateChange}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...a11y}
          />
        )}
      </FormField>
      <FormField label="Notes" htmlFor="notes">
        {() => (
          <Input.TextArea
            name="values.notes"
            id="notes"
            onChange={e => setFieldValue('notes', e.target.value)}
            value={values.notes}
          />
        )}
      </FormField>
    </div>
  );
};

FormSection.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object),
  propertiesLoading: PropTypes.bool,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.objectOf(PropTypes.any).isRequired,
  setTotalArea: PropTypes.func.isRequired,
  setGeoJSONCollectionFeatures: PropTypes.func.isRequired,
  fieldsAndAreasGeoJSONCollection: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object
  ]).isRequired
};

export default FormSection;
